import { Button, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { StepActions, useStepperContext } from "src/components/stepper";
import { BidPackageStatus, BidScopeSource, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { useSaveBidPackagesMutation } from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { createBidPackagesDetailUrl, createBidPackagesUrl, createPlanBasedBidPackageEditUrl } from "src/RouteUrls";
import { fail, formatList } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { PlanBasedBidPackageFormState, PlanBasedBidPackageStep } from "../utils";

type PlanBasedBidPackageStepActionsProps = { formState: ObjectState<PlanBasedBidPackageFormState>; isEdit: boolean };

export function PlanBasedBidPackageStepActions(props: PlanBasedBidPackageStepActionsProps) {
  const { formState, isEdit } = props;
  const { currentStep, nextStep, prevStep, setSteps, steps, isLastStep } = useStepperContext();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const [saveBidPackages, { loading: savingBidPackages }] = useSaveBidPackagesMutation();
  const history = useHistory();
  const currentStepIndex = steps.findIndex((step) => step.value === currentStep.value);
  const nextStepDisabled = steps[currentStepIndex + 1]?.disabled;

  const save = useCallback(
    async (exiting: boolean) => {
      const input = mapToSaveBidPackagesInput(formState);
      const result = await saveBidPackages({ variables: { input: [input] } });
      const saved = result.data?.saveBidPackages.bidPackages?.first;
      const groupId = saved?.bidPackageGroup?.id;
      if (groupId) {
        const requests = (formState.value.requests ?? []).map((r) => ({
          ...r,
          // Pick up assigned IDs from saved requests
          id: r.id ?? saved?.requests?.find((sr) => sr.tradePartner.id === r.tradePartnerId)?.id,
        }));
        const bidPlanGroups = (formState.value.bidPlanGroups ?? [])
          // Drop archived rows from form state, the BE just soft-deleted them. To re-add, the FE must send a fresh (no ID) row
          .filter((g) => !g.archive)
          .map((g) => {
            // Fill in the ids the server just assigned, leave the rest of the row state alone.
            const savedGroup = saved?.bidPlanGroups?.find(
              (sg) => (sg.globalOption?.id ?? null) === (g.globalOptionId ?? null),
            );
            return {
              ...g,
              id: g.id ?? savedGroup?.id,
              dimensions: (g.dimensions ?? []).map((d) => ({
                ...d,
                id: d.id ?? savedGroup?.dimensions.find((sd) => sd.globalOptionType.id === d.globalOptionTypeId)?.id,
              })),
            };
          });
        formState.set({
          ...formState.value,
          bidPackageGroupId: groupId,
          bidPackageId: saved!.id,
          requests,
          bidPlanGroups,
        });
        window.history.replaceState(null, "", createPlanBasedBidPackageEditUrl(groupId, projectId));
      }
      setSteps((prev) => prev.map((s) => (s.value === currentStep.value ? { ...s, state: "complete" } : s)));
      if (exiting) {
        const url = isEdit ? createBidPackagesDetailUrl(groupId!, saved!.id) : createBidPackagesUrl(projectId);
        history.push(url);
      } else {
        nextStep();
      }
    },
    [formState, saveBidPackages, setSteps, projectId, currentStep.value, isEdit, history, nextStep],
  );

  const submitBidPackage = useCallback(async () => {
    const bidPackageId = formState.value.bidPackageId ?? fail("Cannot submit a bid package that has not been saved");
    const groupId = formState.value.bidPackageGroupId ?? fail("Cannot submit a bid package that has not been saved");
    await saveBidPackages({ variables: { input: [{ id: bidPackageId, status: BidPackageStatus.Open }] } });
    history.push(createBidPackagesDetailUrl(groupId, bidPackageId));
  }, [formState, saveBidPackages, history]);

  const isFormValid = useComputed(() => formState.valid, [formState]);
  const continueDisabledReason = nextStepDisabled ? continueReasonByStep(currentStep.value, formState) : undefined;

  return (
    <StepActions>
      <>
        {currentStepIndex > 0 && <Button variant="tertiary" label="Back" size="lg" onClick={prevStep} />}
        <Button
          variant="secondary"
          label={isEdit ? "Save & Exit" : "Save Draft & Exit"}
          size="lg"
          onClick={() => save(true)}
          disabled={savingBidPackages}
        />
        {isLastStep ? (
          <Button
            label="Submit"
            size="lg"
            onClick={submitBidPackage}
            disabled={savingBidPackages || currentStep.disabled || !isFormValid}
          />
        ) : (
          <Button
            label="Continue"
            size="lg"
            onClick={() => save(false)}
            disabled={savingBidPackages || continueDisabledReason}
          />
        )}
      </>
    </StepActions>
  );
}

function continueReasonByStep(step: string, formState: ObjectState<PlanBasedBidPackageFormState>): string | undefined {
  if (step === PlanBasedBidPackageStep.DEFINE_PACKAGE) {
    return "Fill all required fields to continue.";
  }
  if (step === PlanBasedBidPackageStep.CONFIGURE_BID_GROUPS) {
    return formatList(formState.bidPlanGroups.errors);
  }
  return undefined;
}

function mapToSaveBidPackagesInput(formState: ObjectState<PlanBasedBidPackageFormState>): SaveBidPackageInput {
  const {
    bidPackageId,
    bidPackageGroupId,
    marketId,
    projects,
    costCodes,
    costType,
    productOfferings,
    requests,
    bidPlanGroups,
  } = formState.value;
  return {
    id: bidPackageId ?? undefined,
    bidPackageGroupId: bidPackageGroupId ?? undefined,
    projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
    costCodes: costCodes ?? [],
    costType: costType ?? undefined,
    scopeSource: BidScopeSource.ProductOffering,
    isUnitBased: false,
    isInternalEstimate: false,
    isPlanBasedV2: true,
    productOfferings: productOfferings ?? undefined,
    requests: requests?.nonEmpty ? requests : undefined,
    bidPlanGroups: bidPlanGroups
      ?.filter((g) => !g.archive)
      .map((g) => ({
        id: g.id ?? undefined,
        order: g.order ?? undefined,
        globalOptionId: g.globalOptionId ?? undefined,
        dimensions: (g.dimensions ?? []).map((d) => ({
          id: d.id ?? undefined,
          globalOptionTypeId: d.globalOptionTypeId ?? undefined,
          globalOptionIds: d.globalOptionIds ?? [],
        })),
      })),
  };
}
